// A creative's files, on a page of its own: the link Claude hands a person when it has drafted a
// creative and needs its images, video or banners (`GET /creatives/{id}/readiness` names this
// page as `upload_url`). It says exactly which files the channel needs, from the catalogs the
// server enforces (`/creative-specs`), shows what is attached and what still blocks review, and
// opens the creative's own content editor, so the upload, the server's probes and the revision
// rules are the ones every other way in uses. Works signed out: the guard sends the person to
// sign in and back here (routes/creatives/$creativeId.assets.tsx).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Creative } from '@/api/types';
import type { CreativeReadiness, CreativeSpecs } from '@/api/types.gen';
import { creativeById, creativeReadiness, creativeSpecs } from '@/api/resources';
import { CreativeContentEditor } from '@/components/creative-content-editor';
import { QueryErrorNote } from '@/components/error-panel';
import { StatusPill } from '@/components/status-pill';
import { placementLabel } from '@/lib/creative-placement-labels';

const SECTION_LABEL = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-3)',
  margin: '0 0 8px',
  fontWeight: 600,
};

/** A byte limit as written: whole MiB when it is one (Meta's 30 MiB), else MB or KB. */
export const mb = (bytes: number) => {
  const mebibyte = 1024 * 1024;
  if (bytes % mebibyte === 0) return `${bytes / mebibyte} MiB`;
  if (bytes >= 1_000_000) return `${Number((bytes / 1_000_000).toFixed(1))} MB`;
  return `${Number((bytes / 1000).toFixed(1))} KB`;
};
const seconds = (ms: number) => `${ms / 1000} s`;
const mimeWords = (types: readonly string[]) =>
  types.map((t) => (t === 'video/quicktime' ? 'MOV' : (t.split('/')[1] ?? t).toUpperCase())).join(', ');

/** The files this creative's channel needs, as sentences, from the enforced specs. */
export function filesNeeded(creative: Pick<Creative, 'channel' | 'media_type'>, specs: CreativeSpecs): string[] {
  if (creative.channel === 'meta') {
    const { meta } = specs;
    const ratios = meta.formats
      .map((f) => `${f.ratio_label} (at least ${f.min_width}×${f.min_height})`)
      .join(', ');
    const file = creative.media_type === 'video'
      ? `A video for each: ${mimeWords(meta.video.mime_types)}, ${seconds(meta.video.min_duration_ms)} to ${seconds(meta.video.max_duration_ms)}, up to ${mb(meta.video.max_bytes)}; ${meta.video.byte_rules.join('; ')}.`
      : `An image for each: ${mimeWords(meta.image.mime_types)}, up to ${mb(meta.image.max_bytes)}.`;
    return [`At least ${meta.min_formats} of these sizes: ${ratios}.`, file];
  }
  if (creative.channel === 'ctv') {
    const { ctv } = specs;
    return [
      `One 16:9 video, at least ${ctv.min_width}×${ctv.min_height}.`,
      `${ctv.durations_ms.map(seconds).join(' or ')} long (±${seconds(ctv.duration_tolerance_ms)}); ${mimeWords(ctv.mime_types)}, up to ${mb(ctv.max_bytes)}.`,
    ];
  }
  const { display } = specs;
  return [
    `One or more banners, each exactly one of: ${display.sizes.map((s) => `${s.width}×${s.height}`).join(', ')}.`,
    `${mimeWords(display.mime_types)}, up to ${mb(display.max_bytes)} each (${mb(display.recommended_max_bytes)} or less serves on more sites); an animation should stop within ${seconds(display.animation_max_ms)}.`,
  ];
}

export function CreativeAssetsView({ creativeId }: { creativeId: string }) {
  const creative = useQuery(creativeById(creativeId));
  const specs = useQuery(creativeSpecs(undefined));
  const readiness = useQuery(creativeReadiness(creativeId));
  const [editing, setEditing] = useState(false);
  const c = creative.data;

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div style={{ minWidth: 0 }}>
          <Link to="/creatives" search={{ focus: creativeId }} className="muted" style={{ fontSize: 12 }}>
            ← Creatives
          </Link>
          <h1 className="page-title" style={{ overflowWrap: 'anywhere' }}>
            {c ? `Files for ${c.name}` : 'Creative files'}
          </h1>
        </div>
      </div>

      <div role="region" aria-label="Creative files" tabIndex={0} style={{ flex: 1, overflow: 'auto', padding: '22px 22px 60px' }}>
        <div style={{ maxWidth: 760 }}>
          {creative.isError && (
            <QueryErrorNote error={creative.error} what="this creative" onRetry={() => { void creative.refetch(); }} />
          )}
          {creative.isPending && (
            <div className="muted" style={{ padding: '24px 0', fontSize: 13, fontStyle: 'italic' }}>Loading the creative…</div>
          )}
          {c && (
            <CreativeFiles
              creative={c}
              specs={specs.data}
              readiness={readiness.data}
              onEdit={() => setEditing(true)}
            />
          )}
        </div>
      </div>
      {c && editing && (
        <CreativeContentEditor creative={c} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />
      )}
    </div>
  );
}

function CreativeFiles({
  creative,
  specs,
  readiness,
  onEdit,
}: {
  creative: Creative;
  specs: CreativeSpecs | undefined;
  readiness: CreativeReadiness | undefined;
  onEdit: () => void;
}) {
  const draft = creative.status === 'draft';
  const attached = creative.assets.flatMap((asset) => asset.placement_tags);
  return (
    <>
      <div className="panel" style={{ padding: '12px 14px', marginBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}>
        <span style={{ fontWeight: 650, overflowWrap: 'anywhere' }}>{creative.name}</span>
        <span className="mono muted" style={{ fontSize: 11.5 }}>{creative.format}</span>
        <StatusPill status={creative.status} />
      </div>

      {!draft && (
        <p role="note" style={{ fontSize: 13, lineHeight: 1.5 }}>
          This creative has left draft, so its files are no longer changed here. Open it in{' '}
          <Link to="/creatives" search={{ focus: creative.id }}>Creatives</Link> to see its versions.
        </p>
      )}

      {draft && (
        <>
          <section aria-labelledby="creative-files-needed" style={{ marginBottom: 24 }}>
            <h2 id="creative-files-needed" style={SECTION_LABEL}>What this creative needs</h2>
            {specs ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
                {filesNeeded(creative, specs).map((line) => <li key={line}>{line}</li>)}
              </ul>
            ) : (
              <div className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>Loading the file rules…</div>
            )}
          </section>

          <section aria-labelledby="creative-files-attached" style={{ marginBottom: 24 }}>
            <h2 id="creative-files-attached" style={SECTION_LABEL}>Attached</h2>
            {attached.length === 0 ? (
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>No files yet.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
                {attached.map((tag) => <li key={tag}>{placementLabel(tag)}</li>)}
              </ul>
            )}
          </section>

          {readiness && (
            <section aria-labelledby="creative-files-review" style={{ marginBottom: 24 }}>
              <h2 id="creative-files-review" style={SECTION_LABEL}>Ready for review</h2>
              {readiness.ready_for_review ? (
                <p style={{ fontSize: 13, margin: 0 }}>Yes: nothing blocks submitting it for review.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
                  {readiness.problems.map((problem) => <li key={`${problem.code}:${problem.message}`}>{problem.message}</li>)}
                </ul>
              )}
            </section>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button type="button" className="btn btn-primary" onClick={onEdit}>
              Add or replace files
            </button>
            <Link to="/creatives" search={{ focus: creative.id }} className="btn">
              Done
            </Link>
          </div>
        </>
      )}
    </>
  );
}
